import { useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Button,
  Textarea,
  Badge,
  ActionIcon,
  Menu,
  Alert,
  Divider,
  Loader,
  Center,
  Modal,
  Select,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconMessageCircle,
  IconPlus,
  IconDots,
  IconEdit,
  IconTrash,
  IconCheck,
  IconX,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import {
  useCaseComments,
  useCaseDiscussions,
  useAddComment,
  useUpdateComment,
  useDeleteComment,
  useResolveDiscussion,
  useCancelDiscussion,
} from "../../hooks/useInternalDiscussions";
import {
  discussionCategoryLabels,
  discussionCategoryColors,
  DiscussionStatus,
  DiscussionAction,
  type CaseComment,
} from "../../services/internalDiscussions.service";
import { useAuthStore } from "../../store/auth.store";
import { FlagDiscussionModal } from "./FlagDiscussionModal";
import { required, scrollToFirstError } from "../../utils/formValidation";

interface DiscussionCommentsPanelProps {
  caseId: string;
  caseNumber: string;
  workingDiagnosis?: string;
}

interface CommentFormValues {
  content: string;
}

interface ResolveFormValues {
  resolution: string;
  consensusDiagnosis: string;
  recommendations: string;
  actionTaken: string; // DiscussionAction enum value or empty
}

// Action taken options for resolve modal dropdown (sorted alphabetically)
const actionTakenOptions = [
  {
    value: DiscussionAction.ADDITIONAL_STAINS,
    label: "Additional Stains Ordered",
  },
  { value: DiscussionAction.APPROVED, label: "Approved" },
  {
    value: DiscussionAction.EXTERNAL_CONSULT,
    label: "External Consult Requested",
  },
  { value: DiscussionAction.MODIFIED, label: "Modified" },
];

interface CancelFormValues {
  reason: string;
}

export function DiscussionCommentsPanel({
  caseId,
  caseNumber,
  workingDiagnosis,
}: DiscussionCommentsPanelProps) {
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [flagModalOpened, flagModalHandlers] = useDisclosure(false);
  const [resolveModalOpened, resolveModalHandlers] = useDisclosure(false);
  const [cancelModalOpened, cancelModalHandlers] = useDisclosure(false);

  const profile = useAuthStore((state) => state.profile);

  const { data: discussions = [], isLoading: discussionsLoading } =
    useCaseDiscussions(caseId);
  const { data: comments = [], isLoading: commentsLoading } =
    useCaseComments(caseId);

  const addComment = useAddComment();
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();
  const resolveDiscussion = useResolveDiscussion();
  const cancelDiscussion = useCancelDiscussion();

  // Find active discussion (not resolved and not cancelled)
  const activeDiscussion = discussions.find(
    (d) => !d.isResolved && d.status !== DiscussionStatus.CANCELLED
  );
  const isLoading = discussionsLoading || commentsLoading;

  // Add comment form
  const commentForm = useForm<CommentFormValues>({
    initialValues: { content: "" },
    validate: {
      content: required("Comment is required"),
    },
  });

  // Edit comment form
  const editForm = useForm<CommentFormValues>({
    initialValues: { content: "" },
    validate: {
      content: required("Comment is required"),
    },
  });

  // Resolve discussion form
  const resolveForm = useForm<ResolveFormValues>({
    initialValues: {
      resolution: "",
      consensusDiagnosis: "",
      recommendations: "",
      actionTaken: "",
    },
  });

  // Cancel discussion form
  const cancelForm = useForm<CancelFormValues>({
    initialValues: { reason: "" },
  });

  const handleAddComment = commentForm.onSubmit(
    async (values) => {
      try {
        await addComment.mutateAsync({
          caseId,
          content: values.content.trim(),
          discussionId: activeDiscussion?.id,
          isInternal: true,
        });

        commentForm.reset();
        notifications.show({
          title: "Comment Added",
          message: "Your comment has been added",
          color: "green",
        });
      } catch (err: any) {
        notifications.show({
          title: "Error",
          message: err.message || "Failed to add comment",
          color: "red",
        });
      }
    },
    () => scrollToFirstError(commentForm)
  );

  const handleEditComment = async (commentId: string) => {
    const error = editForm.validate();
    if (error.hasErrors) {
      scrollToFirstError(editForm);
      return;
    }

    try {
      await updateComment.mutateAsync({
        commentId,
        data: { content: editForm.values.content.trim() },
      });

      setEditingCommentId(null);
      editForm.reset();
      notifications.show({
        title: "Comment Updated",
        message: "Your comment has been updated",
        color: "green",
      });
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to update comment",
        color: "red",
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm("Are you sure you want to delete this comment?")) return;

    try {
      await deleteComment.mutateAsync(commentId);
      notifications.show({
        title: "Comment Deleted",
        message: "Comment has been deleted",
        color: "green",
      });
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to delete comment",
        color: "red",
      });
    }
  };

  const startEditing = (comment: CaseComment) => {
    setEditingCommentId(comment.id);
    editForm.setFieldValue("content", comment.content);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    editForm.reset();
  };

  const handleResolveDiscussion = async () => {
    if (!activeDiscussion) return;

    try {
      await resolveDiscussion.mutateAsync({
        discussionId: activeDiscussion.id,
        data: {
          resolution: resolveForm.values.resolution.trim() || undefined,
        },
      });

      resolveModalHandlers.close();
      resolveForm.reset();
      notifications.show({
        title: "Discussion Resolved",
        message: "The discussion has been marked as resolved",
        color: "green",
      });
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to resolve discussion",
        color: "red",
      });
    }
  };

  const handleCancelDiscussion = async () => {
    if (!activeDiscussion) return;

    try {
      await cancelDiscussion.mutateAsync({
        discussionId: activeDiscussion.id,
        data: {
          reason: cancelForm.values.reason.trim() || undefined,
        },
      });

      cancelModalHandlers.close();
      cancelForm.reset();
      notifications.show({
        title: "Discussion Cancelled",
        message: "The discussion has been cancelled",
        color: "blue",
      });
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to cancel discussion",
        color: "red",
      });
    }
  };

  return (
    <>
      <Paper withBorder>
        <Stack gap="md">
          {/* Header */}
          <Group justify="space-between" p="md" pb={0}>
            <Group gap="xs">
              <IconMessageCircle size={20} />
              <Text fw={600}>Discussion Comments</Text>
              {activeDiscussion && (
                <Badge
                  color={
                    activeDiscussion.category
                      ? discussionCategoryColors[activeDiscussion.category]
                      : "gray"
                  }
                  variant="light"
                  size="sm"
                >
                  {activeDiscussion.category
                    ? discussionCategoryLabels[activeDiscussion.category]
                    : "Discussion"}
                </Badge>
              )}
            </Group>
            <Button
              size="xs"
              leftSection={<IconMessageCircle size={14} />}
              onClick={flagModalHandlers.open}
              disabled={!!activeDiscussion}
            >
              Flag for Discussion
            </Button>
          </Group>

          <Divider />

          {/* Loading State */}
          {isLoading && (
            <Center p="xl">
              <Loader size="md" />
            </Center>
          )}

          {/* Comments List */}
          {!isLoading && (
            <Stack gap="md" p="md" pt={0}>
              {comments.length === 0 && (
                <Alert color="blue" variant="light">
                  <Text size="sm">
                    {activeDiscussion
                      ? "Discussion started. Be the first to add a comment."
                      : "No comments yet. Add a comment or flag for discussion."}
                  </Text>
                </Alert>
              )}

              {comments.map((comment) => (
                <Paper key={comment.id} p="sm" withBorder>
                  <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                      <Text size="sm" fw={600}>
                        {comment.user?.fullName || "Unknown User"}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {dayjs(comment.createdAt).format("MMM D, YYYY h:mm A")}
                        {comment.updatedAt &&
                          comment.updatedAt !== comment.createdAt &&
                          dayjs(comment.updatedAt).diff(
                            dayjs(comment.createdAt),
                            "second"
                          ) > 1 && (
                            <Text
                              span
                              size="xs"
                              c="dimmed"
                              fs="italic"
                              title={`Edited ${dayjs(comment.updatedAt).format(
                                "MMM D, YYYY h:mm A"
                              )}`}
                            >
                              {" "}
                              (edited)
                            </Text>
                          )}
                      </Text>
                      {comment.internalDiscussionId && (
                        <Badge
                          size="xs"
                          color={
                            comment.internalDiscussion?.category
                              ? discussionCategoryColors[
                                  comment.internalDiscussion.category
                                ]
                              : "blue"
                          }
                          variant="light"
                        >
                          {comment.internalDiscussion?.category
                            ? discussionCategoryLabels[
                                comment.internalDiscussion.category
                              ]
                            : "Discussion"}
                        </Badge>
                      )}
                    </Group>
                    {profile?.id === comment.userId && (
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="sm">
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            onClick={() => startEditing(comment)}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => handleDeleteComment(comment.id)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>

                  {editingCommentId === comment.id ? (
                    <Stack gap="xs">
                      <Textarea
                        {...editForm.getInputProps("content")}
                        minRows={2}
                        autoFocus
                        withAsterisk
                      />
                      <Group gap="xs">
                        <Button
                          size="xs"
                          onClick={() => handleEditComment(comment.id)}
                          loading={updateComment.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={cancelEditing}
                        >
                          Cancel
                        </Button>
                      </Group>
                    </Stack>
                  ) : (
                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {comment.content}
                    </Text>
                  )}
                </Paper>
              ))}
            </Stack>
          )}

          {/* Add Comment - Always visible per C-776 spec */}
          {!isLoading && (
            <>
              <Divider />
              <form onSubmit={handleAddComment}>
                <Stack gap="sm" p="md" pt={0}>
                  <Textarea
                    placeholder={
                      activeDiscussion
                        ? "Add a comment to the discussion..."
                        : "Add a comment to this case..."
                    }
                    minRows={3}
                    withAsterisk
                    {...commentForm.getInputProps("content")}
                  />
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">
                      Comments are internal and not part of the final report.
                    </Text>
                    <Button
                      size="sm"
                      type="submit"
                      leftSection={<IconPlus size={14} />}
                      loading={addComment.isPending}
                    >
                      Add Comment
                    </Button>
                  </Group>
                </Stack>
              </form>
            </>
          )}

          {/* Discussion Action Buttons - GAP 5.4 and 5.3 */}
          {activeDiscussion && !isLoading && (
            <>
              <Divider />
              <Group justify="flex-end" p="md" pt="xs">
                <Button
                  variant="outline"
                  color="red"
                  size="sm"
                  leftSection={<IconX size={14} />}
                  onClick={cancelModalHandlers.open}
                  loading={cancelDiscussion.isPending}
                >
                  Cancel Discussion
                </Button>
                <Button
                  color="green"
                  size="sm"
                  leftSection={<IconCheck size={14} />}
                  onClick={resolveModalHandlers.open}
                  loading={resolveDiscussion.isPending}
                >
                  Resolve Discussion
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Paper>

      {/* Flag Discussion Modal */}
      <FlagDiscussionModal
        opened={flagModalOpened}
        onClose={flagModalHandlers.close}
        caseId={caseId}
        caseNumber={caseNumber}
        workingDiagnosis={workingDiagnosis}
      />

      {/* Resolve Discussion Modal */}
      <Modal
        opened={resolveModalOpened}
        onClose={resolveModalHandlers.close}
        closeOnClickOutside={false}
        title="Resolve Discussion"
        size="lg"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Mark this discussion as resolved. All fields are optional.
          </Text>

          <Divider />

          <Select
            label="Action Taken"
            placeholder="Select action taken..."
            data={actionTakenOptions}
            clearable
            {...resolveForm.getInputProps("actionTaken")}
          />

          <Textarea
            label="Consensus Diagnosis"
            placeholder="Enter the agreed-upon diagnosis..."
            minRows={2}
            {...resolveForm.getInputProps("consensusDiagnosis")}
          />

          <Textarea
            label="Recommendations"
            placeholder="Enter any follow-up recommendations..."
            minRows={2}
            {...resolveForm.getInputProps("recommendations")}
          />

          <Textarea
            label="Resolution Notes"
            placeholder="Describe the outcome of this discussion..."
            minRows={3}
            {...resolveForm.getInputProps("resolution")}
          />

          <Group justify="space-between">
            <Button variant="subtle" onClick={resolveModalHandlers.close}>
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleResolveDiscussion}
              loading={resolveDiscussion.isPending}
              leftSection={<IconCheck size={14} />}
            >
              Resolve
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Cancel Discussion Confirmation Dialog */}
      <Modal
        opened={cancelModalOpened}
        onClose={cancelModalHandlers.close}
        closeOnClickOutside={false}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={20} color="var(--mantine-color-red-6)" />
            <span>Cancel Discussion</span>
          </Group>
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
          >
            Are you sure you want to cancel this discussion? This action cannot
            be undone.
          </Alert>
          <Textarea
            label="Reason for Cancellation (optional)"
            placeholder="Why is this discussion no longer needed?"
            minRows={3}
            {...cancelForm.getInputProps("reason")}
          />
          <Group justify="space-between">
            <Button variant="default" onClick={cancelModalHandlers.close}>
              Keep Discussion
            </Button>
            <Button
              color="red"
              onClick={handleCancelDiscussion}
              loading={cancelDiscussion.isPending}
              leftSection={<IconX size={14} />}
            >
              Yes, Cancel Discussion
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
